/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, May 30th 2022, 2:49:55 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { SSOSignUpInput, SSOSignUpInputValidationSchema, SSOSignUpResponse, User } from "lib/schemas";
import { APISERVICEPATHS, useAPIMutation, useAPIRequest, useAPIFetchItem, useAPIForm } from "lib/api";
import { useLocalSaveLoggedInUser } from "authentication/hooks/useLocalSaveLoggedInUser";
import { getAbsoluteURL } from "core/routes/utils";
import { URLS } from "core/routes";
import { useTranslatedText } from "core";
import { useUserContext } from "authentication/providers/UserContextProvider";

interface GoogleLoginResponse {
  oauth_server_url: string;
  status: string;
}

const googleLoginAPIPath = APISERVICEPATHS.adminGoogleLogin;

export const useAPIGetGoogleOAuthServer = () => {
  const { makeRequest, makeRequestCleanUp, isMakingAPIRequest, ...otherValues } = useAPIRequest<GoogleLoginResponse>({
    apiServicePath: googleLoginAPIPath,
    makeRequestOnInitialMount: false,
    filterByGlobalOrganization: false,
  });

  const [isRedirectingToOAuthServer, setIsRedirectingToOAuthServer] = React.useState<boolean>(false);

  const { clearLoggedInUser } = useUserContext();

  const redirectToGoogleOAuthServer = () => {
    makeRequest({
      setValidAPIResponse: (response: GoogleLoginResponse) => {
        window.location.assign(response.oauth_server_url);
        clearLoggedInUser(); // Clear the logged in user data so we can start afresh.
      },
      apiQueryParams: { oauth_callback_url: getAbsoluteURL(URLS.adminSSOCallback) },
    });
  };

  React.useEffect(() => {
    if (isMakingAPIRequest) setIsRedirectingToOAuthServer(true);
  }, [isMakingAPIRequest]);

  return { redirectToGoogleOAuthServer, isRedirectingToOAuthServer, ...otherValues };
};

interface GoogleSignInResponse {
  signup_required?: boolean;
  message?: string;
}

export const useAPIGoogleSignIn = (authorizationCode: string) => {
  const { fetchUserDetails, isFetchingUserDetails, loggedInUser } = useLocalSaveLoggedInUser();

  const {
    postData,
    isMakingAPIRequest: isSigningInToGoogle,
    apiErrors,
    ...apiMutationState
  } = useAPIMutation<GoogleSignInResponse>({
    apiServicePath: googleLoginAPIPath,
    filterByGlobalOrganization: false,
  });

  const [googleSignInResponse, setGoogleSignInResponse] = React.useState<GoogleSignInResponse>();

  const handleGoogleSignInResponse = (googleSignInResponse: GoogleSignInResponse) => {
    setGoogleSignInResponse(googleSignInResponse);
    const signInIsSuccessful = !googleSignInResponse?.signup_required;
    if (signInIsSuccessful) fetchUserDetails();
  };

  const { oauthLoginClientErrorMessage } = useTranslatedText();

  const non_field_errors: string[] = (apiErrors as any)?.non_field_errors;

  const errorMessage: string = non_field_errors?.length
    ? non_field_errors.join(" | ")
    : apiErrors?.status === "oauth_error" && oauthLoginClientErrorMessage;

  React.useEffect(() => {
    if (authorizationCode) {
      postData({ body: JSON.stringify({ code: authorizationCode }), setValidAPIResponse: handleGoogleSignInResponse });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizationCode]);

  return {
    googleSignInResponse,
    isSigningInToGoogle,
    errorMessage,
    ...apiMutationState,
    fetchUserDetails,
    isFetchingUserDetails,
    loggedInUser,
  };
};

const googleSignupAPIPath = APISERVICEPATHS.adminGoogleSignup;

export const useAPISSOSignUpForm = () => {
  const initialSignUpInput: SSOSignUpInput = {
    organization_name: "",
  };

  const [signUpError, setSignUpError] = React.useState("");

  const [signUpAPIResponse, setSignUpAPIResponse] = React.useState<SSOSignUpResponse>();

  const useAPIFormReturn = useAPIForm<SSOSignUpInput, any>({
    apiInitialFetchId: null,
    primaryKey: null,
    apiFormDefaultValues: initialSignUpInput,
    apiFormDataValidationSchema: SSOSignUpInputValidationSchema,
    setAPIFormResponse: setSignUpAPIResponse,
    apiServicePath: googleSignupAPIPath,
    errorNotificationMessage: `SignUp Failed: ${signUpError}`,
    successNotificationMessage: "Signup Successful. Please wait as we redirecting you...",
  });

  const { apiDataSendErrors, isAPIDataSendError } = useAPIFormReturn;

  React.useEffect(() => {
    if (isAPIDataSendError) {
      setSignUpError(apiDataSendErrors?.detail ?? "");
    }
  }, [isAPIDataSendError, apiDataSendErrors?.detail]);

  return { useAPIFormReturn, ...signUpAPIResponse };
};

interface SSOSignupInitialData {
  initial_data: {
    email: User["email"];
    username: User["username"];
    first_name: User["first_name"];
    last_name: User["last_name"];
  };
}

// Fetches initial SSO sign up data from backend session
export const useAPIFetchSSOSignupInitialData = () => {
  const {
    apiItem: ssoSignupInitialData,
    isMakingAPIRequest: isFetchingSSOInitialData,
    isAPIError: isSSOInitialDataFetchError,
  } = useAPIFetchItem<SSOSignupInitialData>({ apiServicePath: googleSignupAPIPath, makeRequestOnInitialMount: true });

  return { ssoSignupInitialData, isFetchingSSOInitialData, isSSOInitialDataFetchError };
};
